import React, { useState, useEffect, useMemo } from 'react';
import { 
  Settings, 
  Calculator, 
  DollarSign, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  Building,
  Lock,
  Database,
  Loader,
  User,
  Camera,
  CreditCard,
  Image,
  MapPin
} from 'lucide-react';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { MotorbikeIcon } from '../Upload Doc Invoice/Icons';

// Import separated components
import FormFieldsSettings from './FormFieldsSettings';
import CalculationSettings from './CalculationSettings';
import DepositSettings from './DepositSettings';
import AddonsSettings from './AddonsSettings';
import DocumentsSettings from './DocumentsSettings';
import AdvancedSettings from './AdvancedSettings';
import PaymentMethodsSettings from './PaymentMethodsSettings';
import BrandingSettings from './BrandingSettings';
import LocationsSettings from './LocationsSettings';

const ReservationSettings = ({ 
  currentUser, 
  currentCompany, 
  getCompanyContext, 
  hasPermission 
}) => {
  
  // Default addons array - always available
  const defaultAddons = [
    { id: 1, name: 'Passenger Helmet', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 2, name: 'Helmet Premium', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 3, name: 'Adventure Gear', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 4, name: 'Boots', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 5, name: 'Cold Weather Gear', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 6, name: 'Gloves', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 7, name: 'Goggles', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 8, name: 'Jacket', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 9, name: 'Rain Gear', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' },
    { id: 10, name: 'Trousers', category: 'Clothing', enabled: false, price: '0.00', pricingMethod: 'perDay' }
  ];

  // Default document requirements - always available
  const defaultDocuments = [
    { id: 1, name: 'Driving License', category: 'License', required: true, enabled: true, description: 'Valid driving license required for vehicle rental' },
    { id: 2, name: 'Passport', category: 'Identity', required: false, enabled: false, description: 'Government issued passport for identity verification' },
    { id: 3, name: 'National ID', category: 'Identity', required: false, enabled: false, description: 'National identity card or government ID' },
    { id: 4, name: 'Visa', category: 'Travel', required: false, enabled: false, description: 'Tourist or travel visa (for international visitors)' },
    { id: 5, name: 'Credit Card', category: 'Payment', required: false, enabled: false, description: 'Credit card for payment and security deposit' },
    { id: 6, name: 'Insurance Certificate', category: 'Insurance', required: false, enabled: false, description: 'Valid insurance coverage certificate' },
    { id: 7, name: 'International Driving Permit', category: 'License', required: false, enabled: false, description: 'IDP for international visitors' },
    { id: 8, name: 'Proof of Address', category: 'Verification', required: false, enabled: false, description: 'Utility bill or official document showing current address' }
  ];

  // Default delivery locations - always available
  const defaultLocations = [
    { id: 1, name: 'Main Office', deliveryCharge: '0', enabled: true, isDefault: true, category: 'both', description: 'No delivery charge for main office pickup/dropoff' },
    { id: 2, name: 'Colombo Airport', deliveryCharge: '25', enabled: true, isDefault: false, category: 'both', description: 'Bandaranaike International Airport' },
    { id: 3, name: 'Negombo Beach', deliveryCharge: '15', enabled: true, isDefault: false, category: 'both', description: 'Beach area hotels' },
    { id: 4, name: 'Colombo City Center', deliveryCharge: '20', enabled: true, isDefault: false, category: 'both', description: 'Downtown Colombo hotels' },
    { id: 5, name: 'Galle Fort', deliveryCharge: '50', enabled: false, isDefault: false, category: 'both', description: 'Galle Fort area' },
    { id: 6, name: 'Kandy City', deliveryCharge: '40', enabled: false, isDefault: false, category: 'both', description: 'Kandy city hotels' }
  ];

  // Default field settings
  const defaultFieldSettings = {
    customerName: {
      visible: true,
      required: true,
      label: 'Customer Name',
      order: 1,
      icon: 'User',
      placeholder: 'Enter customer name',
      description: 'Full name of the person making the reservation'
    },
    whatsappNumber: {
      visible: true,
      required: false,
      label: 'WhatsApp Number',
      order: 2,
      icon: 'Phone',
      placeholder: 'Enter WhatsApp number',
      description: 'WhatsApp contact number for communication',
      validationPattern: '^[+]?[0-9]{8,15}$'
    },
    email: {
      visible: true,
      required: false,
      label: 'Email',
      order: 3,
      icon: 'Mail',
      placeholder: 'Enter email address',
      description: 'Email address for booking confirmations',
      validationType: 'email'
    },
    licenseNumber: {
      visible: true,
      required: true,
      label: 'License Number',
      order: 4,
      icon: 'Shield',
      placeholder: 'Enter license number',
      description: 'Valid driving license number',
      validationPattern: ''
    },
    pickupDateTime: {
      visible: true,
      required: true,
      label: 'Pickup Date & Time',
      order: 5,
      icon: 'Calendar',
      description: 'When the rental period begins',
      futureOnly: true
    },
    dropoffDateTime: {
      visible: true,
      required: true,
      label: 'Drop-off Date & Time',
      order: 6,
      icon: 'Calendar',
      description: 'When the rental period ends',
      futureOnly: true
    },
    pickupLocation: {
      visible: true,
      required: false,
      label: 'Pickup Location',
      order: 7,
      icon: 'MapPin',
      description: 'Where to collect the motorbike',
      defaultValue: 'Main Office'
    },
    dropoffLocation: {
      visible: true,
      required: false,
      label: 'Drop-off Location',
      order: 8,
      icon: 'MapPin',
      description: 'Where to return the motorbike',
      defaultValue: 'Main Office'
    },
    motorbikeNumber: {
      visible: true,
      required: true,
      label: 'Motorbike',
      order: 9,
      icon: 'Bike',
      description: 'Select the motorbike for rental'
    },
    availabilityStatus: {
      visible: true,
      required: true,
      label: 'Availability Status',
      order: 10,
      icon: 'CheckCircle',
      description: 'Current status of the reservation',
      allowedValues: ['booked', 'pending', 'cancel'],
      defaultValue: 'booked'
    },
    note: {
      visible: true,
      required: false,
      label: 'Note',
      order: 11,
      icon: 'MessageSquare',
      placeholder: 'Enter any notes',
      description: 'Additional information about the reservation',
      maxLength: 500,
      multiline: true
    }
  };

  // State Management - Always initialize with safe defaults
  const [settings, setSettings] = useState({
    // Rental Calculation Settings
    rentalCalculationMethod: 'inclusive',
    minimumRentalDays: 1,
    allowSameDayRental: true,
    autoCalculateAmount: true,
    
    // Multi-Deposit System
    depositOptions: [
      { id: 1, amount: '100', currency: 'USD', symbol: '$', label: '$100', isDefault: true }
    ],
    
    // Rental Add-ons - Always initialize as array
    rentalAddons: [...defaultAddons],
    
    // Document Requirements - Always initialize as array
    documentRequirements: [...defaultDocuments],
    
    // Delivery Locations Configuration - NEW
    deliveryLocations: [...defaultLocations],
    enableDeliveryCharges: true,
    showDeliveryChargeInCalculation: true,
    
    // Payment Methods Configuration
    paymentMethods: [
      { id: 1, name: 'Cash', type: 'cash', enabled: true, description: 'Cash payment at pickup/dropoff', isDefault: true },
      { id: 2, name: 'Credit Card', type: 'credit_card', enabled: true, description: 'Visa, Mastercard, Amex', isDefault: false },
      { id: 3, name: 'Debit Card', type: 'debit_card', enabled: true, description: 'Direct debit payment', isDefault: false },
      { id: 4, name: 'Bank Transfer', type: 'bank_transfer', enabled: false, description: 'Direct bank account transfer', isDefault: false }
    ],
    
    // Branding Configuration
    branding: {
      showLogo: true,
      showCompanyName: true,
      logoSize: 'medium',
      logoPosition: 'center',
      nameSize: 'medium',
      namePosition: 'center'
    },
    
    // Form Field Configuration
    fieldSettings: { ...defaultFieldSettings },
    
    // Default Values
    defaultPickupLocation: 'Main Office',
    defaultDropoffLocation: 'Main Office',
    
    // Business Rules
    advanceBookingDays: 30,
    maxRentalDuration: 30,
    requireCustomerEmail: false,
    requireLicenseNumber: true,
    
    // Validation Rules
    validationRules: {
      whatsappNumberFormat: 'international',
      emailValidation: 'strict',
      licenseNumberPattern: '',
      customerNameMinLength: 2,
      noteMaxLength: 500,
      requirePhoneOrEmail: false,
      allowPastDates: false,
      maxAdvanceBookingDays: 365
    },
    
    // Form Behavior Settings
    formBehavior: {
      showOptionalFieldsToggle: false,
      compactMode: false,
      groupRelatedFields: true,
      enableFieldReordering: false,
      showFieldDescriptions: true,
      showFieldIcons: true,
      enableInlineValidation: true,
      showCharacterCounts: true
    },
    
    // Notifications
    enableEmailNotifications: false,
    enableWhatsappNotifications: false,
    reminderDaysBefore: 1,
    
    // System Settings
    allowCancellations: true,
    allowModifications: true,
    showCalculationDetails: true,
    dataIsolationMode: true,
    allowCameraCapture: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('fields');

  // Get company context
  const companyId = getCompanyContext();

  // Settings collection reference
  const settingsDocRef = useMemo(() => {
    if (!companyId) return null;
    return doc(db, `companies/${companyId}/settings`, 'reservations');
  }, [companyId]);

  // Safe addon access - always returns array
  const getAddonsSafely = () => {
    return Array.isArray(settings.rentalAddons) ? settings.rentalAddons : defaultAddons;
  };

  // Safe filter for enabled addons
  const getEnabledAddons = () => {
    const addons = getAddonsSafely();
    return addons.filter(addon => addon && addon.enabled === true);
  };

  // Safe document access - always returns array
  const getDocumentsSafely = () => {
    return Array.isArray(settings.documentRequirements) ? settings.documentRequirements : defaultDocuments;
  };

  // Safe filter for enabled documents
  const getEnabledDocuments = () => {
    const documents = getDocumentsSafely();
    return documents.filter(document => document && document.enabled === true);
  };

  // Safe filter for required documents
  const getRequiredDocuments = () => {
    const documents = getEnabledDocuments();
    return documents.filter(document => document.required === true);
  };

  // Safe payment methods access - always returns array
  const getPaymentMethodsSafely = () => {
    return Array.isArray(settings.paymentMethods) ? settings.paymentMethods : [];
  };

  // Safe filter for enabled payment methods
  const getEnabledPaymentMethods = () => {
    const methods = getPaymentMethodsSafely();
    return methods.filter(method => method && method.enabled === true);
  };

  // Safe locations access - always returns array
  const getLocationsSafely = () => {
    return Array.isArray(settings.deliveryLocations) ? settings.deliveryLocations : defaultLocations;
  };

  // Safe filter for enabled locations
  const getEnabledLocations = () => {
    const locations = getLocationsSafely();
    return locations.filter(location => location && location.enabled === true);
  };

  // Get pickup locations
  const getPickupLocations = () => {
    const locations = getEnabledLocations();
    return locations.filter(loc => loc.category === 'pickup' || loc.category === 'both');
  };

  // Get dropoff locations
  const getDropoffLocations = () => {
    const locations = getEnabledLocations();
    return locations.filter(loc => loc.category === 'dropoff' || loc.category === 'both');
  };

  // Get visible form fields ordered
  const getVisibleFields = () => {
    const fieldSettings = settings.fieldSettings || defaultFieldSettings;
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => fieldConfig.visible)
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Load settings from Firebase
  const loadSettings = async () => {
    if (!settingsDocRef) {
      setError('No company context available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Loading reservation settings for company:', currentCompany?.name);
      
      const docSnap = await getDoc(settingsDocRef);
      
      if (docSnap.exists()) {
        const loadedSettings = docSnap.data();
        
        // Migrate old single deposit to new multi-deposit system
        if (loadedSettings.defaultDepositAmount && !loadedSettings.depositOptions) {
          const amount = loadedSettings.defaultDepositAmount.replace(/[^0-9.]/g, '');
          const symbol = loadedSettings.defaultDepositAmount.replace(/[0-9.]/g, '');
          const currency = symbol === '$' ? 'USD' : symbol === '€' ? 'EUR' : symbol === '£' ? 'GBP' : 'USD';
          
          loadedSettings.depositOptions = [
            { 
              id: 1, 
              amount: amount || '100', 
              currency: currency, 
              symbol: symbol || '$', 
              label: loadedSettings.defaultDepositAmount || '$100',
              isDefault: true 
            }
          ];
          delete loadedSettings.defaultDepositAmount;
        }

        // Ensure rentalAddons is always an array
        if (!Array.isArray(loadedSettings.rentalAddons)) {
          loadedSettings.rentalAddons = defaultAddons;
        }

        // Ensure documentRequirements is always an array
        if (!Array.isArray(loadedSettings.documentRequirements)) {
          loadedSettings.documentRequirements = defaultDocuments;
        }

        // Ensure deliveryLocations is always an array
        if (!Array.isArray(loadedSettings.deliveryLocations)) {
          loadedSettings.deliveryLocations = defaultLocations;
        }

        // Ensure paymentMethods is always an array
        if (!Array.isArray(loadedSettings.paymentMethods)) {
          loadedSettings.paymentMethods = settings.paymentMethods;
        }

        // Ensure branding settings exist
        if (!loadedSettings.branding) {
          loadedSettings.branding = settings.branding;
        }

        // Ensure fieldSettings exists with defaults
        if (!loadedSettings.fieldSettings) {
          loadedSettings.fieldSettings = defaultFieldSettings;
        } else {
          // Merge with defaults to ensure all fields exist
          loadedSettings.fieldSettings = {
            ...defaultFieldSettings,
            ...loadedSettings.fieldSettings
          };
        }

        // Ensure formBehavior and validationRules exist
        if (!loadedSettings.formBehavior) {
          loadedSettings.formBehavior = settings.formBehavior;
        }
        if (!loadedSettings.validationRules) {
          loadedSettings.validationRules = settings.validationRules;
        }

        // Ensure delivery settings exist
        if (loadedSettings.enableDeliveryCharges === undefined) {
          loadedSettings.enableDeliveryCharges = true;
        }
        if (loadedSettings.showDeliveryChargeInCalculation === undefined) {
          loadedSettings.showDeliveryChargeInCalculation = true;
        }
        
        setSettings(prevSettings => ({
          ...prevSettings,
          ...loadedSettings,
          rentalAddons: loadedSettings.rentalAddons || defaultAddons,
          documentRequirements: loadedSettings.documentRequirements || defaultDocuments,
          deliveryLocations: loadedSettings.deliveryLocations || defaultLocations,
          paymentMethods: loadedSettings.paymentMethods || settings.paymentMethods,
          branding: loadedSettings.branding || settings.branding,
          fieldSettings: loadedSettings.fieldSettings || defaultFieldSettings
        }));
        
        console.log('Loaded settings:', loadedSettings);
      } else {
        console.log('No existing settings found, using defaults');
        await saveDefaultSettings();
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Save default settings to Firebase
  const saveDefaultSettings = async () => {
    if (!settingsDocRef) return;

    try {
      const defaultSettings = {
        ...settings,
        companyId: companyId,
        companyName: currentCompany?.name,
        createdBy: currentUser?.id,
        createdByUsername: currentUser?.username,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(settingsDocRef, defaultSettings);
      console.log('Default settings saved');
    } catch (error) {
      console.error('Error saving default settings:', error);
    }
  };

  // Save settings to Firebase
  const saveSettings = async () => {
    if (!settingsDocRef) {
      setError('No company context available');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      console.log('Saving reservation settings for company:', currentCompany?.name);
      
      const settingsToSave = {
        ...settings,
        companyId: companyId,
        companyName: currentCompany?.name,
        updatedBy: currentUser?.id,
        updatedByUsername: currentUser?.username,
        updatedAt: serverTimestamp(),
        lastModified: new Date().toISOString()
      };

      await updateDoc(settingsDocRef, settingsToSave);
      
      setLastSaved(new Date());
      console.log('Settings saved successfully');
      
      alert(`Reservation settings updated successfully for ${currentCompany?.name}!`);
      
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings: ' + error.message);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle setting changes
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Update field setting
  const updateFieldSetting = (fieldKey, property, value) => {
    const updatedFieldSettings = {
      ...settings.fieldSettings,
      [fieldKey]: {
        ...settings.fieldSettings[fieldKey],
        [property]: value
      }
    };
    
    // If changing required status, sync with legacy settings
    if (property === 'required') {
      if (fieldKey === 'email') {
        handleSettingChange('requireCustomerEmail', value);
      } else if (fieldKey === 'licenseNumber') {
        handleSettingChange('requireLicenseNumber', value);
      }
    }
    
    handleSettingChange('fieldSettings', updatedFieldSettings);
  };

  // Load settings on component mount
  useEffect(() => {
    if (currentCompany && currentUser && companyId) {
      loadSettings();
    }
  }, [currentCompany, currentUser, companyId]);

  // Sync field settings with legacy settings
  useEffect(() => {
    if (settings.fieldSettings) {
      const emailField = settings.fieldSettings.email;
      const licenseField = settings.fieldSettings.licenseNumber;
      
      if (emailField && emailField.required !== settings.requireCustomerEmail) {
        updateFieldSetting('email', 'required', settings.requireCustomerEmail);
      }
      if (licenseField && licenseField.required !== settings.requireLicenseNumber) {
        updateFieldSetting('licenseNumber', 'required', settings.requireLicenseNumber);
      }
    }
  }, [settings.requireCustomerEmail, settings.requireLicenseNumber]);

  // Permission check
  if (!hasPermission('reservations') && !hasPermission('admin')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <Lock className="mx-auto h-20 w-20 text-gray-400 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
            <p className="text-gray-500 text-lg">You don't have permission to access reservation settings.</p>
            <p className="text-gray-600 text-sm mt-2">Contact your company administrator for access.</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-white text-lg">Loading Reservation Settings...</p>
              <p className="text-gray-400 text-sm mt-2">
                {currentCompany ? `Loading settings for ${currentCompany.name}` : 'Loading company context...'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* HEADER */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Settings size={28} className="text-orange-500 mr-3" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-purple-600">
              Reservation Settings
            </span>
            <div className="ml-4 flex items-center text-green-400 text-sm">
              <Lock size={16} className="mr-1" />
              <span>Company Configuration</span>
            </div>
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center text-blue-400 text-sm">
              <Building size={16} className="mr-1" />
              <span>{currentCompany?.name}</span>
            </div>
          </div>
        </div>

        {/* ERROR DISPLAY */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle size={20} className="text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">Settings Error</p>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* COMPANY STATUS PANEL */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4 flex items-start gap-3 mb-6">
          <div className="bg-orange-500/20 p-2 rounded-lg">
            <Database size={20} className="text-orange-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1 flex items-center">
              <Lock className="mr-2" size={16} />
              Complete Form Field Control System Active
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 text-sm">
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-orange-300 font-medium flex items-center mb-1">
                  <Building className="mr-1" size={14} />
                  Company Context
                </div>
                <div className="text-gray-300">{currentCompany?.name}</div>
                <div className="text-xs text-gray-400">ID: {companyId?.slice(-8)}</div>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-green-300 font-medium flex items-center mb-1">
                  <User className="mr-1" size={14} />
                  Form Fields
                </div>
                <div className="text-gray-300">{getVisibleFields().length} visible</div>
                <div className="text-xs text-gray-400">
                  {getVisibleFields().filter(([, config]) => config.required).length} required
                </div>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-red-300 font-medium flex items-center mb-1">
                  <DollarSign className="mr-1" size={14} />
                  Deposits
                </div>
                <div className="text-gray-300">{settings.depositOptions.length} options</div>
                <div className="text-xs text-gray-400">
                  Default: {settings.depositOptions.find(d => d.isDefault)?.label || 'None'}
                </div>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-blue-300 font-medium flex items-center mb-1">
                  <MotorbikeIcon className="mr-1" size={14} />
                  Add-ons
                </div>
                <div className="text-gray-300">{getEnabledAddons().length} enabled</div>
                <div className="text-xs text-gray-400">
                  {getAddonsSafely().length} total
                </div>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-purple-300 font-medium flex items-center mb-1">
                  <Camera className="mr-1" size={14} />
                  Documents
                </div>
                <div className="text-gray-300">{getEnabledDocuments().length} enabled</div>
                <div className="text-xs text-gray-400">
                  {getRequiredDocuments().length} required
                </div>
              </div>
              <div className="bg-gray-800/50 rounded p-3">
                <div className="text-yellow-300 font-medium flex items-center mb-1">
                  <MapPin className="mr-1" size={14} />
                  Locations
                </div>
                <div className="text-gray-300">{getEnabledLocations().length} enabled</div>
                <div className="text-xs text-gray-400">
                  {getPickupLocations().length} pickup / {getDropoffLocations().length} dropoff
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABS NAVIGATION */}
        <div className="flex flex-wrap gap-2 mb-6 bg-gray-800/50 p-2 rounded-lg border border-gray-700">
          {[
            { id: 'fields', label: 'Form Fields', icon: User },
            { id: 'calculation', label: 'Calculation', icon: Calculator },
            { id: 'deposits', label: 'Deposits', icon: DollarSign },
            { id: 'addons', label: 'Add-ons', icon: MotorbikeIcon },
            { id: 'documents', label: 'Documents', icon: Camera },
            { id: 'locations', label: 'Locations', icon: MapPin },
            { id: 'payment', label: 'Payment', icon: CreditCard },
            { id: 'branding', label: 'Branding', icon: Image },
            { id: 'advanced', label: 'Advanced', icon: Settings }
          ].map(tab => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <IconComponent size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="space-y-6">
          
          {/* FORM FIELDS CONFIGURATION */}
          {activeTab === 'fields' && (
            <FormFieldsSettings
              settings={settings}
              defaultFieldSettings={defaultFieldSettings}
              handleSettingChange={handleSettingChange}
              updateFieldSetting={updateFieldSetting}
              currentCompany={currentCompany}
            />
          )}

          {/* RENTAL CALCULATION SETTINGS */}
          {activeTab === 'calculation' && (
            <CalculationSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* MULTI-DEPOSIT MANAGEMENT */}
          {activeTab === 'deposits' && (
            <DepositSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* RENTAL ADD-ONS */}
          {activeTab === 'addons' && (
            <AddonsSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* DOCUMENT REQUIREMENTS */}
          {activeTab === 'documents' && (
            <DocumentsSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* DELIVERY LOCATIONS */}
          {activeTab === 'locations' && (
            <LocationsSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* PAYMENT METHODS */}
          {activeTab === 'payment' && (
            <PaymentMethodsSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* BRANDING CONFIGURATION */}
          {activeTab === 'branding' && (
            <BrandingSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
            />
          )}

          {/* ADVANCED SETTINGS */}
          {activeTab === 'advanced' && (
            <AdvancedSettings
              settings={settings}
              handleSettingChange={handleSettingChange}
              currentCompany={currentCompany}
            />
          )}

          {/* SAVE ACTIONS - Fixed at bottom */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mt-8">
            <div className="p-6 border-b border-gray-700">
              <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                <Save className="w-5 h-5 text-orange-400" />
                Save Settings
              </h4>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  {lastSaved && (
                    <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 flex items-center">
                      <CheckCircle size={16} className="text-green-400 mr-2" />
                      <div>
                        <div className="text-green-300 text-sm font-medium">Settings Saved</div>
                        <div className="text-green-400 text-xs">
                          {lastSaved.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* System Info */}
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-gray-400">Fields:</span>
                        <span className="text-white">{getVisibleFields().length} visible</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-400">Add-ons:</span>
                        <span className={getEnabledAddons().length > 0 ? 'text-green-400' : 'text-red-400'}>
                          {getEnabledAddons().length > 0 
                            ? `${getEnabledAddons().length} enabled` 
                            : 'None enabled'
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-gray-400">Documents:</span>
                        <span className={getEnabledDocuments().length > 0 ? 'text-green-400' : 'text-red-400'}>
                          {getEnabledDocuments().length > 0 
                            ? `${getEnabledDocuments().length} enabled` 
                            : 'None enabled'
                          }
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-400">Locations:</span>
                        <span className={getEnabledLocations().length > 0 ? 'text-green-400' : 'text-red-400'}>
                          {getEnabledLocations().length > 0 
                            ? `${getEnabledLocations().length} locations` 
                            : 'None'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={loadSettings}
                    disabled={saving || loading}
                    className="flex items-center gap-2 bg-gray-700 text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw size={16} />
                    <span>Reload</span>
                  </button>
                  
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        <span>Saving Settings...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>Save All Settings</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Configuration Summary */}
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h5 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Info size={16} className="text-orange-400" />
                  Configuration Summary
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="bg-gray-800/50 rounded p-3">
                    <div className="text-orange-300 font-medium mb-1">Calculation</div>
                    <div className="text-gray-300 capitalize">{settings.rentalCalculationMethod}</div>
                    <div className="text-xs text-gray-400">
                      {settings.autoCalculateAmount ? 'Auto-calc' : 'Manual'}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-3">
                    <div className="text-green-300 font-medium mb-1">Deposits</div>
                    <div className="text-gray-300">{settings.depositOptions.length} options</div>
                    <div className="text-xs text-gray-400">
                      {settings.depositOptions.find(d => d.isDefault)?.label}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-3">
                    <div className="text-blue-300 font-medium mb-1">Locations</div>
                    <div className="text-gray-300">{getEnabledLocations().length} enabled</div>
                    <div className="text-xs text-gray-400">
                      {settings.enableDeliveryCharges ? 'Charges enabled' : 'No charges'}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-3">
                    <div className="text-purple-300 font-medium mb-1">Validation</div>
                    <div className="text-gray-300">
                      {settings.validationRules?.emailValidation || 'Strict'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {settings.validationRules?.requirePhoneOrEmail ? 'Contact required' : 'No contact req'}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded p-3">
                    <div className="text-yellow-300 font-medium mb-1">Features</div>
                    <div className="text-gray-300">
                      {settings.allowCameraCapture ? 'Camera OK' : 'No Camera'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {settings.dataIsolationMode ? 'Isolated' : 'Shared'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationSettings;